import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { API_URLS } from "../../config/apiUrls";
import { AdSegment } from "../../../domain/adSegments";

// Tipos para as props do Anuncio (inalterados)
interface AnuncioProps {
  anuncio: {
    id: string;
    title: string;
    description: string;
    reasonStatus: string;
    status: string;
    value: string | null;
    offer: string;
    address: string;
    site: string;
    email: string;
    wpp: string;
    youtube: string;
    instagram: string;
    facebook: string;
    apple_st: string;
    play_st: string;
    location: string | null;
    approvedAt: string | null;
    expiresAt: string | null;
    financialRegister: string | null;
    segment: AdSegment | null;
    category: {
      name: string;
    };
    // A API passou a devolver de 1 a 3 subcategorias por anúncio (array),
    // no lugar do antigo objeto único `subcategory`.
    subcategories: Array<{
      id: string;
      name: string;
    }>;
    images: Array<{
      imageUrl: string;
      orderIndex: number;
    }>;
    createdAt: string;
    updatedAt: string;
    isCountryWide: boolean;
    user: {
      name: string;
    };
    payments: Array<{
      status: string;
    }>;
    ufs: Array<{
      id: number;
      name: string;
      abbreviation: string;
    }>;
    cities: Array<{
      id: string;
      name: string;
    }>;
    comments: Array<{
      comment: string;
    }>;
  };
  token: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
  onAdUpdated: () => void;
}

type SubcategoriaDoAnuncio = { id: string; name: string };

// Anúncios criados antes da migração para múltiplas subcategorias podem chegar
// sem o array; nesses casos mostramos um traço em vez de quebrar a tela.
const formatarSubcategorias = (
  subcategorias?: SubcategoriaDoAnuncio[],
): string =>
  subcategorias?.length
    ? subcategorias.map((subcategoria) => subcategoria.name).join(", ")
    : "—";

const subcategoriaLabel = (subcategorias?: SubcategoriaDoAnuncio[]): string =>
  (subcategorias?.length ?? 0) > 1 ? "Subcategorias" : "Subcategoria";

const Anuncio: React.FC<AnuncioProps> = ({
  anuncio,
  token,
  role,
  onAdUpdated,
}) => {
  const [selectedImage, setSelectedImage] = useState<string>(
    anuncio.images[0]?.imageUrl || "",
  );
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [status, setStatus] = useState<string>(anuncio.status);
  // Novo estado para o modal de confirmação
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [statusToChange, setStatusToChange] = useState<string | null>(null);
  const [reason, setReason] = useState<string>("");
  const [reasonError, setReasonError] = useState<string>("");

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  // Sincroniza os estados locais com as props quando elas mudam
  useEffect(() => {
    setStatus(anuncio.status);
  }, [anuncio.status]);

  const isExpired = anuncio.expiresAt
    ? new Date(anuncio.expiresAt) < new Date()
    : false;

  useEffect(() => {
    const image = anuncio.images.find((image) => image.orderIndex === 1);
    if (image) {
      setSelectedImage(image.imageUrl);
    }
  }, [anuncio.images]);

  // Funções de manipulação (inalteradas)
  const handleModalMainImage = (imageUrl: string) => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleDeleteDialog = () => {
    setOpenDeleteConfirmation(true);
    setOpenDeleteDialog(false);
  };

  const handleDeleteAd = async () => {
    const url = `${API_URLS.ads}/${anuncio.id}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        console.error("Anúncio deletado com sucesso!");
        onAdUpdated();
      } else {
        console.error("Erro ao deletar anúncio:", response.statusText);
      }
    } catch (error) {
      console.error("Erro na requisição:", error);
    }

    setOpenDeleteConfirmation(false);
  };

  const handleStatusChange = (novoStatus: string) => {
    setStatusToChange(novoStatus);

    setReason("");

    if (novoStatus === "APPROVED") {
      setReason("Anúncio Aprovado");
    }

    setOpenConfirmDialog(true);
  };

  function capitalizeWord(word: string) {
    if (!word) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }

  function capitalizeSentence(sentence: string) {
    return sentence.split(" ").map(capitalizeWord).join(" ");
  }

  const confirmarAlteracaoStatus = async () => {
    if ((statusToChange !== "APPROVED" && !reason.trim()) || reason === "") {
      setReasonError("O campo razão é obrigatório.");
      return;
    }

    setReasonError("");

    if (!statusToChange) return;

    const url = `${API_URLS.gerenciarAds}/${anuncio.id}`;
    let statusCode: number;

    switch (statusToChange) {
      case "APPROVED":
        statusCode = 1;
        break;
      case "REJECTED":
        statusCode = 2;
        break;
      case "BLOCKED":
        statusCode = 3;
        break;
      default:
        console.error("Status inválido");
        return;
    }

    const body = JSON.stringify({
      status: statusCode,
      reasonStatus: reason,
    });

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body,
      });

      if (response.ok) {
        setStatus(statusToChange);
        console.log("Status alterado com sucesso!");
        onAdUpdated();
      } else {
        console.error("Erro ao alterar status:", response.statusText);
      }
    } catch (error) {
      console.error("Erro na requisição:", error);
    }

    setOpenConfirmDialog(false);
  };

  const cancelarAlteracaoStatus = () => {
    setOpenConfirmDialog(false);
  };

  const formatDate = (date: string | null): string => {
    if (!date) {
      return "Data não disponível";
    }

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return "Data inválida";
    }

    const formattedDate = parsedDate.toLocaleString("pt-BR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    return formattedDate;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "blue";
      case "APPROVED":
        return "green";
      case "REJECTED":
        return "orange";
      case "BLOCKED":
        return "red";
      default:
        return "grey";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "PENDING":
        return "PENDENTE";
      case "APPROVED":
        return "APROVADO";
      case "REJECTED":
        return "REJEITADO";
      case "BLOCKED":
        return "BLOQUEADO";
      default:
        return "PENDENTE";
    }
  };

  const getPaymentStatus = (payments: Array<any>) => {
    switch (payments.length > 0 ? payments[0].status : "NÃO INFORMADO") {
      case "PENDING":
        return "PENDENTE";
      case "COMPLETED":
        return "CONCLUÍDO";
      case "REJECTED":
        return "REJEITADO";
      case "BLOCKED":
        return "BLOQUEADO";
      default:
        return "NÃO INFORMADO";
    }
  };

  return (
    <Card sx={{ margin: 2, maxWidth: "100%" }}>
      {/* Imagem principal */}
      <CardMedia
        component="img"
        height="200"
        image={`${API_URLS.baseUrl}${selectedImage}`}
        alt={anuncio.title}
        sx={{ cursor: "pointer" }}
        onClick={() =>
          handleModalMainImage(`${API_URLS.baseUrl}${selectedImage}`)
        }
      />

      <CardContent
        sx={{
          position: "relative",
          paddingTop: "30px",
        }}
      >
        <Box
          sx={{
            display: "block",
            position: "absolute",
            top: "4px",
            right: "8px",
            backgroundColor: "#cfe9ff",
            color: "#001120",
            padding: "4px 8px",
            borderRadius: "4px",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "12px",
          }}
        >
          {anuncio.financialRegister}
        </Box>
        <Typography gutterBottom variant="h5" component="div">
          {anuncio.title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          <strong>Descrição:</strong> {anuncio.description}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          <strong>Oferta:</strong> {anuncio.offer}
        </Typography>
        {anuncio.address && (
          <Typography variant="body2" color="textSecondary">
            <strong>Endereço:</strong> {anuncio.address}
          </Typography>
        )}
        {/* Exibe a data do ultimo update ou o aprovado em até expira em conforme o status do anuncio */}
        {anuncio.status !== "APPROVED" && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              <strong>Última atualização:</strong>{" "}
              {formatDate(anuncio.updatedAt)}
            </Typography>
          </Box>
        )}
        {anuncio.status === "APPROVED" && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              <strong>Anúncio aprovado em:</strong>{" "}
              {formatDate(anuncio.approvedAt)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Expira em:</strong> {formatDate(anuncio.expiresAt)}
            </Typography>
          </Box>
        )}
        {/* Exibe um box conforme o anúncio está dentro da validade ou não */}
        {anuncio.status === "APPROVED" && isExpired && (
          <Box
            sx={{
              display: "inline-block",
              backgroundColor: "#e6b8af",
              color: "#5b0f00",
              padding: "4px 8px",
              borderRadius: "4px",
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            Este anúncio expirou.
          </Box>
        )}
        {anuncio.status === "APPROVED" && !isExpired && (
          <Box
            sx={{
              display: "inline-block",
              backgroundColor: "#cfe2f3",
              color: "#073763",
              padding: "4px 8px",
              borderRadius: "4px",
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            Este anúncio é válido.
          </Box>
        )}
        {/* Exibe o Status com cor dinâmica */}
        <Box mt={2}>
          <Typography variant="body2" sx={{ display: "inline-block" }}>
            <strong>Status: </strong>
            <Box
              sx={{
                display: "inline-block",
                backgroundColor: getStatusColor(status),
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                textAlign: "center",
                fontWeight: "bold",
              }}
            >
              <strong>{getStatusLabel(status)}</strong>
            </Box>
          </Typography>
          {/* Exibe a informação sobre o motivo do anuncio ter sido rejeitado ou bloqueado */}
          {anuncio.status !== "APPROVED" && anuncio.status !== "PENDING" && (
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                <strong>
                  Motivo do anúncio {getStatusLabel(status).toLowerCase()}:
                </strong>
                {anuncio.reasonStatus}
              </Typography>
            </Box>
          )}
        </Box>
        {/* Exibe a categoria fixa e suas subcategorias */}
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            <strong>Categoria:</strong> {anuncio.category.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>
              {subcategoriaLabel(anuncio.subcategories)}:
            </strong>{" "}
            {formatarSubcategorias(anuncio.subcategories)}
          </Typography>
        </Box>
        {/* Exibe Links (inalterado) */}
        <Box mt={2}>
          {anuncio.site && (
            <Typography variant="body2" color="textSecondary">
              <strong>Site:</strong>{" "}
              <a href={anuncio.site} target="_blank">
                {anuncio.site}
              </a>
            </Typography>
          )}
          {anuncio.email && (
            <Typography variant="body2" color="textSecondary">
              <strong>Email:</strong> {anuncio.email}
            </Typography>
          )}
          {anuncio.wpp && (
            <Typography variant="body2" color="textSecondary">
              <strong>Whatsapp:</strong> {anuncio.wpp}
            </Typography>
          )}
          {anuncio.youtube && (
            <Typography variant="body2" color="textSecondary">
              <strong>Youtube:</strong>{" "}
              <a href={anuncio.youtube} target="_blank">
                {anuncio.youtube}
              </a>
            </Typography>
          )}
          {anuncio.instagram && (
            <Typography variant="body2" color="textSecondary">
              <strong>Instagram:</strong>{" "}
              <a href={anuncio.instagram} target="_blank">
                {anuncio.instagram}
              </a>
            </Typography>
          )}
          {anuncio.facebook && (
            <Typography variant="body2" color="textSecondary">
              <strong>Facebook:</strong>{" "}
              <a href={anuncio.facebook} target="_blank">
                {anuncio.facebook}
              </a>
            </Typography>
          )}
        </Box>
        {/* Box de informações da região do anúncio (inalterado) */}
        <Box mt={2}>
          {anuncio.isCountryWide && (
            <Box>
              <Typography variant="body2" color="textSecondary">
                <strong>Região que o anúncio será veiculado:</strong>
              </Typography>
              <Box
                sx={{
                  display: "inline-block",
                  backgroundColor: "#d9ead3",
                  color: "#274e13",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  marginRight: "4px",
                  marginBottom: "4px",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                <strong>Brasil</strong>
              </Box>
            </Box>
          )}
          {anuncio.ufs && anuncio.ufs.length > 0 && (
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                <strong>Anúncio será veiculado no estado:</strong>
              </Typography>
              {anuncio.ufs.map((uf, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "inline-block",
                    backgroundColor: "#fff2cc",
                    color: "#7f6000",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    marginRight: "4px",
                    marginBottom: "4px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  <strong>{uf.name}</strong>
                </Box>
              ))}
            </Box>
          )}
          {anuncio.cities && anuncio.cities.length > 0 && (
            <Box mt={2}>
              {anuncio.cities.length > 1 ? (
                <Typography variant="body2" color="textSecondary">
                  <strong>O anúncio será veiculado nas cidades:</strong>
                </Typography>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  <strong>O anúncio será veiculado na cidade:</strong>
                </Typography>
              )}

              {anuncio.cities.map((citie, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "inline-block",
                    backgroundColor: "#c9daf8",
                    color: "#073763",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    marginRight: "4px",
                    marginBottom: "4px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  {capitalizeSentence(citie.name)}
                </Box>
              ))}
            </Box>
          )}
        </Box>
        {/* Exibe o nome do usuário e status de pagamento (inalterado) */}
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            <strong>Usuário:</strong> {anuncio.user.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Status do pagamento:</strong>{" "}
            {getPaymentStatus(anuncio.payments)}
          </Typography>
        </Box>
        {/* Botões para alterar o status (inalterado) */}
        {role !== "PARTNER" && (
          <Box mt={2} sx={{ display: "block", textAlign: "center" }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ maxWidth: "30%" }}
              onClick={() => handleStatusChange("APPROVED")}
              disabled={
                status === "APPROVED" ||
                status === "REJECTED" ||
                status === "BLOCKED"
              }
            >
              Aprovar
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleStatusChange("REJECTED")}
              disabled={status === "REJECTED" || status === "APPROVED"}
              sx={{ marginLeft: 1, maxWidth: "30%" }}
            >
              Rejeitar
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleStatusChange("BLOCKED")}
              disabled={status === "BLOCKED" || status === "APPROVED"}
              sx={{ marginLeft: 1, maxWidth: "30%" }}
            >
              Bloquear
            </Button>
          </Box>
        )}
        {role === "MASTER" && (
          <Box mt={2} sx={{ display: "block", textAlign: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOpenDeleteDialog(true)}
              sx={{ maxWidth: "95%" }}
            >
              Mais Configurações
            </Button>
          </Box>
        )}
        {/* Exibe as miniaturas das imagens - SUBSTITUIÇÃO DO GRID */}
        <Box
          mt={2}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            margin: "-4px", // Simula spacing={1} (8px, compensado com -4px)
          }}
        >
          {anuncio.images
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map((image, orderIndex) => (
              // Substitui <Grid item xs={4}> por Box com largura de 33.333% e padding de 4px
              <Box
                key={orderIndex}
                sx={{
                  width: "33.333%", // 3 itens por linha (xs=4)
                  padding: "4px", // Simula spacing={1}
                }}
              >
                <CardMedia
                  component="img"
                  image={`${API_URLS.baseUrl}${image.imageUrl}`}
                  alt={`Imagem ${orderIndex + 1}`}
                  sx={{
                    height: 100,
                    objectFit: "cover",
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectedImage(image.imageUrl)}
                />
              </Box>
            ))}
        </Box>
      </CardContent>

      {/* Modais de imagem, confirmação de status, e deleção (inalterados) */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md">
        <DialogTitle>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseModal}
            aria-label="close"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <img
            src={`${API_URLS.baseUrl}${selectedImage}`}
            alt="Imagem em tamanho real"
            style={{
              width: "100%",
              height: "auto",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openConfirmDialog} onClose={cancelarAlteracaoStatus}>
        <DialogTitle>Confirmar Alteração de Status</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Tem certeza de que deseja alterar o status para{" "}
            <strong>{getStatusLabel(statusToChange || "")}</strong>?
          </Typography>
          {statusToChange !== "APPROVED" && (
            <TextField
              fullWidth
              label="Razão"
              multiline
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              sx={{ marginTop: 2 }}
              error={!!reasonError}
              helperText={reasonError}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelarAlteracaoStatus} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={confirmarAlteracaoStatus}
            variant="contained"
            color="primary"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        sx={{ overflow: "none", padding: "10px", textAlign: "center" }}
      >
        <IconButton
          edge="end"
          color="secondary"
          onClick={() => setOpenDeleteDialog(false)}
          aria-label="close"
          sx={{
            position: "absolute",
            right: 8,
            top: -4,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ marginTop: 4 }}>Opções para o anúncio:</DialogTitle>
        <DialogContent>
          <Button
            variant="outlined"
            onClick={() => handleDeleteDialog()}
            color="error"
            sx={{ marginTop: "50px" }}
          >
            Deletar Anúncio
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDeleteConfirmation}
        onClose={() => setOpenDeleteConfirmation(false)}
        sx={{ overflow: "none", padding: "10px", textAlign: "center" }}
      >
        <DialogTitle sx={{ marginTop: 4 }}>
          Tem certeza que deseja excluir o anúncio " {anuncio.title}"?
        </DialogTitle>
        <DialogContent>
          <Typography>Esse processo é um irreversível.</Typography>
        </DialogContent>
        <DialogContent>
          <Button
            variant="outlined"
            onClick={() => setOpenDeleteConfirmation(false)}
            color="secondary"
            sx={{ marginRight: 4 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => handleDeleteAd()}
            color="error"
          >
            Deletar Anúncio
          </Button>
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>

    </Card>
  );
};

export default Anuncio;
